import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { Header } from "../components/Header";
import { SparkyChat } from "../components/SparkyChat";
import { UnlockModal } from "../components/UnlockModal";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { bundleSavings } from "../lib/pricing";
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, GraduationCap, Sparkles, Bot, MessageCircleQuestion, Unlock as UnlockIcon } from "lucide-react";

export default function ModuleViewer() {
  const { courseId, moduleId } = useParams();
  const { t, lang, isRTL } = useI18n();
  const navigate = useNavigate();

  const [mod, setMod] = useState(null);
  const [locked, setLocked] = useState(null); // { reason }
  const [busy, setBusy] = useState(false);
  const [completedNow, setCompletedNow] = useState(false);
  const [toast, setToast] = useState(null);
  const [sparkyOpen, setSparkyOpen] = useState(false);
  const [sparkyHook, setSparkyHook] = useState(null);
  const [unlockModal, setUnlockModal] = useState(null); // { scope, moduleId? }
  const [savings, setSavings] = useState(null);

  useEffect(() => {
    setMod(null);
    setLocked(null);
    setCompletedNow(false);
    api
      .getModule(moduleId)
      .then((m) => setMod(m))
      .catch((e) => {
        if (e.status === 403 && e.reason) {
          setLocked({ reason: e.reason });
        } else if (e.status === 401) {
          navigate(`/login?next=${encodeURIComponent(`/courses/${courseId}/modules/${moduleId}`)}`);
        } else {
          setLocked({ reason: e.message });
        }
      });
  }, [moduleId, courseId, navigate]);

  if (locked) {
    const reasonText =
      locked.reason === "payment_required"
        ? t("moduleViewer.lockedPaid")
        : locked.reason === "exam_locked_phase_5"
        ? t("moduleViewer.lockedExam")
        : locked.reason === "not_enrolled"
        ? t("moduleViewer.lockedEnroll")
        : locked.reason;

    const showUnlock = locked.reason === "payment_required";

    // Fetch bundle savings once (lazy) — depends on both quote types.
    if (showUnlock && savings === null) {
      Promise.all([
        api.unlockQuote({ course_id: courseId, scope: "single_module", module_id: moduleId }).catch(() => null),
        api.unlockQuote({ course_id: courseId, scope: "full_course" }).catch(() => null),
      ]).then(([s, f]) => {
        if (s && f) {
          setSavings(bundleSavings({
            unitDisplay: s.amount_display,
            bundleDisplay: f.amount_display,
            paidCount: f.module_ids.length,
          }));
        } else {
          setSavings(false);
        }
      });
    }

    return (
      <div className="min-h-screen bg-[#FDFBF7]" data-testid="module-locked">
        <Header />
        <div className="mx-auto max-w-3xl px-6 md:px-10 py-20 text-center">
          <div className="mx-auto h-16 w-16 rounded-full bg-[#FEF3C7] text-[#7A4B00] grid place-items-center">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="mt-6 text-3xl md:text-4xl font-black">{t("moduleViewer.lockedTitle")}</h1>
          <p className="mt-3 text-black/70 font-medium max-w-md mx-auto" data-testid="module-locked-reason">
            {reasonText}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            {locked.reason === "exam_locked_phase_5" && (
              <Link
                to={`/courses/${courseId}/exam`}
                className="inline-flex items-center gap-2 rounded-full bg-[#4C1D95] text-white font-bold px-6 py-3 shadow-[0_4px_0_#2E1065] hover:translate-y-[1px] hover:shadow-[0_3px_0_#2E1065] transition"
                data-testid="module-locked-goto-exam"
              >
                <GraduationCap className="h-4 w-4" /> {t("exam.startCta")}
              </Link>
            )}
            {showUnlock && (
              <>
                <button
                  onClick={() => setUnlockModal({ scope: "single_module", moduleId })}
                  className="inline-flex items-center gap-2 rounded-full bg-[#4361EE] text-white font-bold px-6 py-3 shadow-[0_4px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_3px_0_#2A3EB1] transition"
                  data-testid="module-locked-unlock-single"
                >
                  <UnlockIcon className="h-4 w-4" /> {t("courseDetail.unlockThis")} — $4.99
                </button>
                <button
                  onClick={() => setUnlockModal({ scope: "full_course" })}
                  className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-5 py-3 text-sm transition"
                  data-testid="module-locked-unlock-full"
                >
                  {t("courseDetail.unlockAll")} — $29
                </button>
              </>
            )}
            <Link
              to={`/courses/${courseId}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-6 py-3 shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
              data-testid="module-locked-back"
            >
              <ArrowLeft className="h-4 w-4" /> {t("moduleViewer.lockedCtaCourse")}
            </Link>
          </div>
          {showUnlock && savings && savings !== false && savings.saves > 0 && (
            <div
              className="mt-4 text-xs font-bold text-[#166534] tracking-tight"
              data-testid="bundle-savings"
            >
              {t("unlock.savings")} ${savings.saves.toFixed(2)} · {lang === "ar" ? savings.formatAr : savings.formatEn}
            </div>
          )}
        </div>
        <UnlockModal
          open={!!unlockModal}
          onClose={() => {
            setUnlockModal(null);
            // Refetch the module only when the user closes the success screen
            // (via "Open module"). This preserves the confetti + success moment.
            setLocked(null);
            setMod(null);
            api.getModule(moduleId).then(setMod).catch(() => {
              setLocked({ reason: "payment_required" });
            });
          }}
          scope={unlockModal?.scope}
          courseId={courseId}
          moduleId={unlockModal?.moduleId}
          onUnlocked={() => {
            // Intentionally no-op — refetch happens on modal close (unlock-done click)
            // so the "Unlocked ✨" success screen + confetti actually get seen.
          }}
        />
      </div>
    );
  }

  if (!mod) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header />
        <div className="mx-auto max-w-3xl px-6 py-24">
          <div className="h-96 rounded-3xl bg-white animate-pulse border-2 border-black/5" />
        </div>
      </div>
    );
  }

  const completed = completedNow || mod.status === "completed";

  async function onComplete() {
    setBusy(true);
    try {
      const res = await api.completeModule(moduleId);
      setCompletedNow(true);
      // Client-only confetti + toast — no backend involvement
      confetti({
        particleCount: 140,
        spread: 90,
        origin: { y: 0.3 },
        colors: ["#4361EE", "#FFB703", "#F72585", "#4CC9F0", "#E07A5F"],
      });
      const p = res?.progress;
      if (p) {
        const msg = t("moduleViewer.completeToast")
          .replace("{done}", p.completed_count)
          .replace("{total}", p.total_modules);
        setToast(msg);
        setTimeout(() => setToast(null), 3800);
      }
    } finally {
      setBusy(false);
    }
  }

  function openSparky(hook) {
    setSparkyHook(hook || null);
    setSparkyOpen(true);
  }

  const ArrowNext = isRTL ? ArrowLeft : ArrowRight;
  const ArrowPrev = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="module-viewer-page">
      <Header />
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-10 md:py-14">
        <Link
          to={`/courses/${courseId}`}
          className="inline-flex items-center gap-2 text-sm font-bold text-black/60 hover:text-[#4361EE]"
          data-testid="module-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> {t("moduleViewer.backToCourse")}
        </Link>

        <div className="mt-4 text-xs font-black uppercase tracking-wider text-black/50">
          {t("moduleViewer.moduleLabel")} {mod.order}
        </div>
        <h1 className="mt-1 text-3xl md:text-5xl font-black tracking-tight" data-testid="module-title">
          {_bi(mod.title, lang)}
        </h1>
        <p className="mt-3 text-lg text-black/70 font-medium">
          {_bi(mod.summary, lang)}
        </p>

        <div className="mt-8 space-y-4" data-testid="module-content">
          {(mod.content_blocks || []).map((b, i) => (
            <Block key={i} block={b} lang={lang} t={t} onAskSparky={openSparky} />
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {!completed ? (
            <button
              onClick={onComplete}
              disabled={busy}
              className="rounded-full bg-[#4361EE] hover:bg-[#4361EE]/90 disabled:opacity-60 text-white font-bold px-6 py-3.5 shadow-[0_4px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_3px_0_#2A3EB1] transition"
              data-testid="module-complete-btn"
            >
              {busy ? "…" : t("moduleViewer.complete")}
            </button>
          ) : (
            <span
              className="inline-flex items-center gap-2 rounded-full bg-[#DCFCE7] text-[#166534] font-black px-5 py-3"
              data-testid="module-completed-badge"
            >
              <CheckCircle2 className="h-5 w-5" /> {t("moduleViewer.completed")}
            </span>
          )}

          <div className="sm:ms-auto flex items-center gap-2">
            {mod.prev_module && (
              <button
                onClick={() => navigate(`/courses/${courseId}/modules/${mod.prev_module.module_id}`)}
                className="rounded-full bg-white border-2 border-black/10 hover:border-black/30 font-bold px-4 py-2.5 text-sm transition inline-flex items-center gap-2"
                data-testid="module-prev-btn"
              >
                <ArrowPrev className="h-4 w-4" /> {t("moduleViewer.prevModule")}
              </button>
            )}
            {mod.next_module && (
              <button
                onClick={() => navigate(`/courses/${courseId}/modules/${mod.next_module.module_id}`)}
                className="rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-2.5 text-sm shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition inline-flex items-center gap-2"
                data-testid="module-next-btn"
              >
                {t("moduleViewer.nextModule")} <ArrowNext className="h-4 w-4" />
                {mod.next_module.type === "paid" && <Lock className="h-3.5 w-3.5 opacity-70" />}
                {mod.next_module.type === "exam" && <GraduationCap className="h-3.5 w-3.5 opacity-70" />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Floating "Ask Sparky" button */}
      <button
        onClick={() => openSparky(null)}
        className="fixed bottom-6 end-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#4361EE] text-white font-bold px-5 py-3 shadow-[0_6px_0_#2A3EB1] hover:translate-y-[1px] hover:shadow-[0_5px_0_#2A3EB1] transition"
        data-testid="sparky-fab"
      >
        <MessageCircleQuestion className="h-5 w-5" /> {t("moduleViewer.askSparkyFree")}
      </button>

      {/* Toast */}
      {toast && (
        <div
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 rounded-full bg-[#1A1A1A] text-white font-bold px-6 py-3 shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
          data-testid="complete-toast"
        >
          {toast}
        </div>
      )}

      <SparkyChat
        open={sparkyOpen}
        onClose={() => setSparkyOpen(false)}
        courseId={courseId}
        moduleId={moduleId}
        hookPrompt={sparkyHook}
      />
    </div>
  );
}

function _bi(field, lang) {
  // Safely read a bilingual { en, ar } field. Falls back EN↔AR, then "".
  if (!field || typeof field !== "object") return "";
  return field[lang] || field.en || field.ar || "";
}

function Block({ block, lang, t, onAskSparky }) {
  try {
    if (!block || typeof block !== "object") return null;

    if (block.kind === "text") {
      const body = _bi(block.body, lang);
      if (!body) return null;
      return (
        <p className="text-lg leading-relaxed text-[#1A1A1A]" data-testid="block-text">
          {body}
        </p>
      );
    }
    if (block.kind === "activity") {
      // Schema: { kind:"activity", instructions:{en,ar}, prompt?:{en,ar} }
      // `prompt` is optional; only render the heading if present, else fall
      // back to the activity label. Access via _bi guards against missing
      // fields (Phase 7 AI-authored blocks may omit `prompt`).
      const instructions = _bi(block.instructions, lang);
      const heading = _bi(block.prompt, lang) || t("moduleViewer.activity");
      return (
        <div
          className="rounded-3xl bg-[#FFF3C4] border-2 border-[#F4D06F]/50 p-6"
          data-testid="block-activity"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-[#FFB703] font-black px-3 py-1 text-xs uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> {t("moduleViewer.activity")}
          </div>
          <p className="mt-3 text-lg font-bold text-[#1A1A1A]">{heading}</p>
          {instructions && (
            <p className="mt-1 text-sm text-black/70">{instructions}</p>
          )}
        </div>
      );
    }
    if (block.kind === "sparky_hook") {
      const promptText = _bi(block.prompt, lang);
      if (!promptText) return null;
      return (
        <div
          className="rounded-3xl bg-gradient-to-br from-[#E7EEFE] to-[#DFF6FD] border-2 border-[#4361EE]/20 p-6"
          data-testid="block-sparky"
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-[#4361EE] text-white font-black px-3 py-1 text-xs uppercase tracking-wider">
            <Bot className="h-3.5 w-3.5" /> {t("moduleViewer.askSparky")}
          </div>
          <p className="mt-3 text-lg font-bold text-[#1A1A1A]">{promptText}</p>
          <button
            onClick={() => onAskSparky(promptText)}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-white font-bold px-5 py-2.5 text-sm shadow-[0_3px_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_#000] transition"
            data-testid="block-sparky-ask"
          >
            <MessageCircleQuestion className="h-4 w-4" /> {t("moduleViewer.askSparkyAbout")}
          </button>
        </div>
      );
    }
    if (block.kind === "image" && block.url) {
      return (
        <img
          src={block.url}
          alt={_bi(block.alt, lang)}
          className="rounded-3xl w-full border-2 border-black/5"
          data-testid="block-image"
        />
      );
    }
    return null;
  } catch (err) {
    // A single malformed block must NEVER blank the entire lesson page.
    // Log to console for debugging and render a small, non-fatal placeholder.
    // eslint-disable-next-line no-console
    console.error("Block render failed", { kind: block?.kind, err });
    return (
      <div
        className="rounded-2xl border border-black/10 bg-black/5 px-4 py-3 text-xs font-semibold text-black/50"
        data-testid="block-error"
      >
        (This block couldn't be rendered — the rest of the lesson is unaffected.)
      </div>
    );
  }
}
